import { useEffect, useRef, useState } from 'react';
import type { BackupRecord, BackupScope } from '../../shared/types';
import { fmtAgo, fmtBytes, fmtDate, useApi } from '../lib/hooks';
import { useMe } from '../lib/me';
import { api } from '../lib/api';
import { Alert, Badge, Button, ConfirmTyped, Empty, ErrorBox, Field, Loading, Modal, Panel, Toggle, useAction } from '../components/ui';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const scopeKey = (s: BackupScope) => (s.kind === 'tool' ? `tool:${s.toolId}` : s.kind);
const parseKey = (k: string): BackupScope => (k.startsWith('tool:') ? { kind: 'tool', toolId: k.slice(5) } : { kind: k as 'all' | 'platform' });
const TRIGGER_TONE: Record<string, any> = { manual: 'blue', nightly: 'neutral', weekly: 'neutral', safety: 'amber', upload: 'gold' };

export function BackupsPage() {
  const { data, error, loading, reload } = useApi<any>('/api/admin/backups');
  const { can, me } = useMe();
  const { busy, run } = useAction();
  const [scopeFilter, setScopeFilter] = useState('any');
  const [newScope, setNewScope] = useState('all');
  const [restoreOf, setRestoreOf] = useState<BackupRecord | null>(null);
  const [purge, setPurge] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox error={error} retry={reload} />;
  if (!data) return null;
  const manage = can('admin.backups.manage');
  const admin = !!me?.access.isAdmin;
  const sch = schedule ?? data.schedule;
  const toolName = (id: string) => data.tools.find((t: any) => t.id === id)?.name ?? id;
  const scopes = [{ k: 'all', label: 'Everything' }, { k: 'platform', label: 'Platform (dashboard core)' }, ...data.tools.map((t: any) => ({ k: `tool:${t.id}`, label: `Tool: ${t.name}` }))];
  const list: BackupRecord[] = data.backups.filter((b: BackupRecord) => scopeFilter === 'any' || scopeKey(b.scope) === scopeFilter);

  return (
    <div className="stack">
      {data.maintenance?.active && <Alert kind="warn">A restore or purge of <b>{data.maintenance.scope}</b> started {fmtAgo(data.maintenance.startedAt)} by {data.maintenance.by}. Non-admins see a maintenance message until it finishes.
        {admin && <> If it is stuck (the server restarted), <button className="link-btn" onClick={() => run('unlock', async () => { await api.post('/api/admin/backups/unlock'); await reload(); }, 'Lock cleared')}>clear the lock</button>. Locks also expire after 30 minutes.</>}</Alert>}
      <div className="grid-2">
        <Panel title="Create a backup" icon="backup">
          <Field label="What to back up">
            <select value={newScope} onChange={(e) => setNewScope(e.target.value)} disabled={!manage}>
              {scopes.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
            </select>
          </Field>
          <p className="muted small">Backups are compressed JSON archives stored in {data.storage === 'gcs' ? 'Cloud Storage' : 'local storage (development)'}. The System Log is included but never overwritten by a restore.</p>
          <div className="panel-foot">
            {admin && <>
              <input type="file" ref={fileRef} hidden accept=".gz,.json,application/gzip,application/json" onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) run('upload', async () => { await api.upload('/api/admin/backups/upload', f); await reload(); }, 'Backup uploaded and verified');
                e.target.value = '';
              }} />
              <Button icon="upload" busy={busy === 'upload'} onClick={() => fileRef.current?.click()}>Upload backup file</Button>
            </>}
            {manage && <Button variant="primary" icon="backup" busy={busy === 'create'} onClick={() => run('create', async () => { await api.post('/api/admin/backups', { scope: parseKey(newScope) }); await reload(); }, 'Backup created')}>Back up now</Button>}
          </div>
        </Panel>

        <Panel title="Automatic backups" icon="refresh">
          <div className="sched">
            <div className="sched-row"><Toggle checked={sch.nightlyEnabled} disabled={!manage} onChange={(v) => setSchedule({ ...sch, nightlyEnabled: v })} label="Nightly" /><span>Nightly, everything</span>
              <label>keep <input type="number" min={1} value={sch.nightlyRetentionDays} disabled={!manage} onChange={(e) => setSchedule({ ...sch, nightlyRetentionDays: Number(e.target.value) })} /> days</label></div>
            <div className="sched-row"><Toggle checked={sch.weeklyEnabled} disabled={!manage} onChange={(v) => setSchedule({ ...sch, weeklyEnabled: v })} label="Weekly" /><span>Weekly on
              <select value={sch.weeklyDay} disabled={!manage} onChange={(e) => setSchedule({ ...sch, weeklyDay: Number(e.target.value) })}>{DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></span>
              <label>keep <input type="number" min={1} value={sch.weeklyRetentionWeeks} disabled={!manage} onChange={(e) => setSchedule({ ...sch, weeklyRetentionWeeks: Number(e.target.value) })} /> weeks</label></div>
            <div className="sched-row"><span className="toggle-spacer" /><span>Run at</span>
              <label><select value={sch.nightlyHour} disabled={!manage} onChange={(e) => setSchedule({ ...sch, nightlyHour: Number(e.target.value) })}>{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}</select> {data.timezone}</label></div>
            <div className="sched-row"><span className="toggle-spacer" /><span>Safety backups (before restore/purge)</span>
              <label>keep <input type="number" min={1} value={sch.safetyRetentionDays} disabled={!manage} onChange={(e) => setSchedule({ ...sch, safetyRetentionDays: Number(e.target.value) })} /> days</label></div>
          </div>
          <p className="muted small">Last automatic run: {fmtAgo(data.lastRun?.lastRunAt)}{data.lastRun?.lastResult && data.lastRun.lastResult !== 'ok' ? ` (${data.lastRun.lastResult})` : ''}. Manual and uploaded backups are kept until deleted.</p>
          {manage && <div className="panel-foot"><Button disabled={!schedule} busy={busy === 'sched'} onClick={() => run('sched', async () => { await api.put('/api/admin/backups/schedule', schedule); setSchedule(null); await reload(); }, 'Schedule saved')}>Save schedule</Button></div>}
        </Panel>
      </div>

      <Panel title="Backups" icon="backup" pad={false} actions={
        <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)} aria-label="Filter by scope">
          <option value="any">All scopes</option>{scopes.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
        </select>}>
        {list.length === 0 ? <Empty icon="backup" title="No backups yet" /> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Backup</th><th>Type</th><th>Size</th><th>Documents</th><th>Created</th><th>Expires</th><th /></tr></thead>
              <tbody>
                {list.map((b) => (
                  <tr key={b.id}>
                    <td><strong>{b.label}</strong><div className="muted small">v{b.platformVersion}{Object.keys(b.toolVersions).length ? ` · ${Object.entries(b.toolVersions).map(([k, v]) => `${toolName(k)} ${v}`).join(', ')}` : ''}</div></td>
                    <td><Badge tone={TRIGGER_TONE[b.trigger]}>{b.trigger}</Badge></td>
                    <td className="nowrap">{fmtBytes(b.sizeBytes)}</td>
                    <td>{Object.values(b.counts).reduce((a, c) => a + c, 0)}</td>
                    <td title={fmtDate(b.createdAt)}>{fmtAgo(b.createdAt)}<div className="muted small">{b.createdBy}</div></td>
                    <td className="muted">{b.expiresAt ? fmtDate(b.expiresAt, false) : 'kept'}</td>
                    <td className="right">
                      <div className="row-actions">
                        {manage && <a className="btn btn-ghost" href={`/api/admin/backups/${b.id}/download`} title="Download"><span>Download</span></a>}
                        {admin && <Button variant="ghost" onClick={() => setRestoreOf(b)}>Restore</Button>}
                        {manage && <Button variant="ghost" icon="trash" aria-label="Delete" busy={busy === b.id}
                          onClick={() => confirm('Delete this backup permanently?') && run(b.id, async () => { await api.del(`/api/admin/backups/${b.id}`); await reload(); }, 'Backup deleted')} />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {admin && (
        <Panel title="Danger zone: purge data" icon="warn" className="danger-zone">
          <p className="muted">Purging deletes all data in a scope after taking a safety backup. Purging the platform keeps settings, the System Log, backups and admin accounts so you are never locked out.</p>
          <div className="chips">
            {scopes.map((s) => <Button key={s.k} variant="danger" onClick={() => setPurge(s.k)}>Purge {s.label}</Button>)}
          </div>
        </Panel>
      )}

      {restoreOf && <RestoreModal backup={restoreOf} toolName={toolName} onClose={() => setRestoreOf(null)} onDone={reload} />}
      {purge && (
        <ConfirmTyped title={`Purge ${scopes.find((s) => s.k === purge)?.label}`} word={`purge ${purge === 'all' ? 'everything' : purge === 'platform' ? 'platform' : purge.slice(5)}`}
          confirmLabel="Purge data" onClose={() => setPurge(null)}
          onConfirm={async () => { await api.post('/api/admin/backups/purge', { scope: parseKey(purge), confirm: `purge ${purge === 'all' ? 'everything' : purge === 'platform' ? 'platform' : purge.slice(5)}` }); await reload(); }}>
          <Alert kind="danger">This permanently deletes the data in this scope. A safety backup is created first and kept for {data.schedule.safetyRetentionDays} days.</Alert>
        </ConfirmTyped>
      )}
    </div>
  );
}

function RestoreModal({ backup, toolName, onClose, onDone }: { backup: BackupRecord; toolName: (id: string) => string; onClose: () => void; onDone: () => void }) {
  const options: { k: string; label: string }[] = backup.scope.kind === 'all'
    ? [{ k: 'all', label: 'Everything' }, { k: 'platform', label: 'Platform only' }, ...Object.keys(backup.toolVersions).map((t) => ({ k: `tool:${t}`, label: `Tool: ${toolName(t)}` }))]
    : [{ k: scopeKey(backup.scope), label: backup.scope.kind === 'platform' ? 'Platform' : `Tool: ${toolName((backup.scope as any).toolId)}` }];
  const [target, setTarget] = useState(options[0].k);
  const [preview, setPreview] = useState<any>(null);
  const [confirmText, setConfirmText] = useState('');
  const [pvErr, setPvErr] = useState<Error | null>(null);
  const { busy, run } = useAction();

  useEffect(() => {
    let live = true;
    setPreview(null); setPvErr(null); setConfirmText('');
    api.post(`/api/admin/backups/${backup.id}/preview`, { scope: parseKey(target) })
      .then((r) => live && setPreview(r)).catch((e) => live && setPvErr(e));
    return () => { live = false; };
  }, [backup.id, target]);

  return (
    <Modal wide title={`Restore: ${backup.label}`} onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="danger" disabled={!preview || confirmText !== preview.confirmWord} busy={busy === 'restore'}
          onClick={() => run('restore', async () => { await api.post(`/api/admin/backups/${backup.id}/restore`, { scope: parseKey(target), confirm: confirmText }); onDone(); onClose(); }, 'Restore complete')}>Restore</Button></>}>
      <Field label="Restore which part">
        <select value={target} onChange={(e) => setTarget(e.target.value)}>
          {options.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
        </select>
      </Field>
      <ErrorBox error={pvErr} />
      {!preview && !pvErr && <Loading label="Checking backup" />}
      {preview && <>
        {preview.warnings.map((w: string) => <Alert key={w} kind="warn">{w}</Alert>)}
        <table className="table compact">
          <thead><tr><th>Collection</th><th>Now</th><th>After restore</th></tr></thead>
          <tbody>{preview.rows.map((r: any) => (
            <tr key={r.collection}><td className="mono">{r.collection}</td><td>{r.current}</td><td>{r.preserved ? <span className="muted">kept as is</span> : r.incoming}</td></tr>
          ))}</tbody>
        </table>
        <Alert kind="danger">Current data in this scope is replaced. A safety backup is taken first. Non-admins see a maintenance message while it runs.</Alert>
        <Field label={<>Type <code className="confirm-word">{preview.confirmWord}</code> to confirm</>}><input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} spellCheck={false} /></Field>
      </>}
    </Modal>
  );
}
